
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Clock, Calendar, Smartphone, Monitor } from "lucide-react";
import { AdminSettings } from "@/types/adminSettings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";

interface CalendarDefaultsTabProps {
  settings: AdminSettings;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
}

export const CalendarDefaultsTab: React.FC<CalendarDefaultsTabProps> = ({
  settings,
  updateSettings,
  saveSettings,
  isSaving
}) => {
  const businessHours = settings.calendar_defaults?.business_hours || [{
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '09:00',
    endTime: '17:00'
  }];

  const timeRange = settings.calendar_defaults?.time_range || {
    minTime: '08:00:00',
    maxTime: '20:00:00'
  };

  const slotDuration = settings.calendar_defaults?.slot_duration || '00:30:00';
  const initialView = settings.calendar_defaults?.initial_view || 'timeGridDay';
  const mobileView = settings.calendar_defaults?.mobile_view || 'timeGridDay';

  const handleBusinessHoursChange = (index: number, field: string, value: any) => {
    const updatedBusinessHours = [...businessHours];
    updatedBusinessHours[index] = {
      ...updatedBusinessHours[index],
      [field]: value
    };

    updateSettings(['calendar_defaults', 'business_hours'], updatedBusinessHours);
  };

  const handleTimeRangeChange = (field: string, value: string) => {
    updateSettings(['calendar_defaults', 'time_range', field], value);
  };

  const getDayNames = (days: number[]) => {
    const dayMap: { [key: number]: string } = {
      0: "Sunday",
      1: "Monday",
      2: "Tuesday",
      3: "Wednesday",
      4: "Thursday",
      5: "Friday",
      6: "Saturday"
    };
    return days.map(day => dayMap[day]).join(", ");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Calendar Default Settings</CardTitle>
        <CardDescription>
          Configure default calendar settings for all services. Individual services can override these settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Business Hours
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Set the default business hours for all services. These will be used unless a service specifies its own hours.
          </p>

          {businessHours.map((hours, index) => (
            <div key={index} className="space-y-4 border p-4 rounded-md">
              <div className="flex items-center justify-between">
                <Label>Working Days</Label>
                <div className="grid grid-cols-7 gap-1">
                  {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                    const isSelected = hours.daysOfWeek.includes(day);
                    const dayLabel = ["S", "M", "T", "W", "T", "F", "S"][day];
                    
                    return (
                      <button
                        key={day}
                        type="button"
                        className={`w-8 h-8 rounded-full text-xs font-medium ${
                          isSelected 
                            ? "bg-primary text-primary-foreground" 
                            : "bg-muted text-muted-foreground"
                        }`}
                        onClick={() => {
                          const newDays = isSelected
                            ? hours.daysOfWeek.filter(d => d !== day)
                            : [...hours.daysOfWeek, day].sort();
                          handleBusinessHoursChange(index, 'daysOfWeek', newDays);
                        }}
                      >
                        {dayLabel}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`start-time-${index}`}>Start Time</Label>
                  <Input
                    id={`start-time-${index}`}
                    type="time"
                    value={hours.startTime}
                    onChange={(e) => handleBusinessHoursChange(index, 'startTime', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`end-time-${index}`}>End Time</Label>
                  <Input
                    id={`end-time-${index}`}
                    type="time"
                    value={hours.endTime}
                    onChange={(e) => handleBusinessHoursChange(index, 'endTime', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}

          <h3 className="text-lg font-medium flex items-center gap-2 mt-6">
            <Calendar className="h-5 w-5 text-primary" />
            Calendar Display Settings
          </h3>
          
          <div className="space-y-4 border p-4 rounded-md">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min-time">Earliest Time Displayed</Label>
                <Input
                  id="min-time"
                  type="time"
                  value={timeRange.minTime.substring(0, 5)}
                  onChange={(e) => handleTimeRangeChange('minTime', `${e.target.value}:00`)}
                />
                <p className="text-xs text-muted-foreground">The earliest time shown on the calendar</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-time">Latest Time Displayed</Label>
                <Input
                  id="max-time"
                  type="time"
                  value={timeRange.maxTime.substring(0, 5)}
                  onChange={(e) => handleTimeRangeChange('maxTime', `${e.target.value}:00`)}
                />
                <p className="text-xs text-muted-foreground">The latest time shown on the calendar</p>
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <Label htmlFor="slot-duration">Default Time Slot Duration</Label>
              <Select 
                value={slotDuration} 
                onValueChange={(val) => updateSettings(['calendar_defaults', 'slot_duration'], val)}
              >
                <SelectTrigger id="slot-duration">
                  <SelectValue placeholder="Select time slot duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="00:15:00">15 minutes</SelectItem>
                  <SelectItem value="00:30:00">30 minutes</SelectItem>
                  <SelectItem value="01:00:00">1 hour</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Default duration for bookable time slots</p>
            </div>

            <Separator className="my-4" />

            <div className="space-y-4">
              <h4 className="font-medium">Default Calendar Views</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1" htmlFor="initial-view">
                    <Monitor className="h-4 w-4" /> Desktop View
                  </Label>
                  <Select 
                    value={initialView} 
                    onValueChange={(val) => updateSettings(['calendar_defaults', 'initial_view'], val)}
                  >
                    <SelectTrigger id="initial-view">
                      <SelectValue placeholder="Select default view" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dayGridMonth">Month</SelectItem>
                      <SelectItem value="timeGridWeek">Week</SelectItem>
                      <SelectItem value="timeGridDay">Day</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Default view on desktop devices</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-1" htmlFor="mobile-view">
                    <Smartphone className="h-4 w-4" /> Mobile View
                  </Label>
                  <Select 
                    value={mobileView} 
                    onValueChange={(val) => updateSettings(['calendar_defaults', 'mobile_view'], val)}
                  >
                    <SelectTrigger id="mobile-view">
                      <SelectValue placeholder="Select mobile view" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dayGridMonth">Month</SelectItem>
                      <SelectItem value="timeGridWeek">Week</SelectItem>
                      <SelectItem value="timeGridDay">Day</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Default view on mobile devices</p>
                </div>
              </div>
            </div>
          </div>

          <Button 
            onClick={saveSettings} 
            disabled={isSaving}
            className="mt-6"
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
